import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  BdSchwerpunkt,
} from '@bindoc-projects/clinic-domain-lib';
import {
  BD_SCHWERPUNKT_DISPLAY_NAME
} from '@bindoc-projects/clinic-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_SCHWERPUNKT_CONDITION_ID = 'schwerpunkt';

export const FilterConditionSchwerpunktType: FilterConditionPluginSpecification<
  BdSchwerpunkt[],
  FilterConditionSchwerpunktDto,
  BdSchwerpunktCondition
> = {
  type: 'schwerpunkt',
  category: 'person',
  displayName: { de: 'Schwerpunkt' },
  sortingIndex: 9,
  occurrence: 'single',
  fromObject(obj: FilterConditionSchwerpunktDto): FilterConditionSchwerpunktPlugin {
    return new FilterConditionSchwerpunktPlugin((obj.includes ?? []) as BdSchwerpunkt[]);
  },
  fromCondition(
    jsonObj: BdSchwerpunktCondition,
  ): FilterConditionSchwerpunktPlugin {
    return new FilterConditionSchwerpunktPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionSchwerpunktPlugin {
    return new FilterConditionSchwerpunktPlugin([]);
  },
};

export class FilterConditionSchwerpunktPlugin extends FilterConditionPluginImpl<
  BdSchwerpunkt[],
  FilterConditionSchwerpunktDto
> {
  public static override Specification = FilterConditionSchwerpunktType;

  constructor(private includes: BdSchwerpunkt[]) {
    super(FilterConditionSchwerpunktType.type);
  }

  override clone(): FilterConditionSchwerpunktPlugin {
    return new FilterConditionSchwerpunktPlugin([...this.includes]);
  }

  override equals(other: FilterConditionSchwerpunktPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.includes, other.includes)
    );
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens einen Schwerpunkt auswählen';
    } else {
      return '';
    }
  }

  override toCondition(): BdCondition {
    return new BdSchwerpunktCondition(this.includes, []);
  }

  override toJSON(): FilterConditionSchwerpunktDto {
    return {
      type: this.type,
      includes: this.includes,
    };
  }

  override toText(): string {
    return this.includes.length === 1
      ? 'ein Schwerpunkt'
      : `${this.includes.length} Schwerpunkte`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    return {
      title,
      items: this.includes.map(
        (id) => BD_SCHWERPUNKT_DISPLAY_NAME[id] ?? id,
      ),
    };
  }

  override toValue(): BdSchwerpunkt[] {
    return this.includes;
  }
}

class BdSchwerpunktCondition extends BdContentCondition<BdSchwerpunkt> {
  constructor(includes: BdSchwerpunkt[], excludes: BdSchwerpunkt[]) {
    super(BD_SCHWERPUNKT_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionSchwerpunktDto {
  type: string;
  includes: string[];
}
